/**
 * Notepad
 *
 * TipTap-powered free-form notes editor for treatment check-in v2.
 * Auto-saves via debounced PATCH after 500 ms of inactivity.
 *
 * Follows the NotebookEditor pattern: content is set once on mount via
 * useEditor's `content` option. Subsequent saves do NOT invalidate the
 * RTK Query cache, so the editor cursor is never reset mid-typing.
 */
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { useUpdateOperatorNotesMutation } from '@/services/api/treatmentCheckInV2Api';

// ── Types ────────────────────────────────────────────────────────────────────

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface NotepadProps {
  checkInId: string;
  matterId: string;
  initialContent?: string;
  className?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export const Notepad = ({
  checkInId,
  matterId,
  initialContent = '',
  className,
}: NotepadProps) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [updateOperatorNotes] = useUpdateOperatorNotesMutation();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedContentRef = useRef(initialContent);
  const checkInIdRef = useRef(checkInId);

  // Keep refs in sync
  useEffect(() => {
    checkInIdRef.current = checkInId;
  }, [checkInId]);

  // Stable save function
  const save = useCallback(
    async (html: string) => {
      if (html === lastSavedContentRef.current) return;
      setSaveStatus('saving');
      try {
        await updateOperatorNotes({
          matterId,
          checkInId: checkInIdRef.current,
          operatorNotes: html,
        }).unwrap();
        lastSavedContentRef.current = html;
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    },
    [matterId, updateOperatorNotes],
  );

  // Debounced handler called on every content change
  const handleUpdate = useCallback(
    (html: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        save(html).catch(() => {});
      }, 500);
    },
    [save],
  );

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Start typing your notes...',
      }),
    ],
    content: initialContent || '',
    onUpdate: ({ editor: e }) => {
      handleUpdate(e.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-3',
      },
    },
  });

  // Flush pending save on unmount instead of discarding it
  const editorRef = useRef(editor);
  editorRef.current = editor;

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        // Flush: immediately save whatever the editor currently contains
        const currentHtml = editorRef.current?.getHTML();
        if (currentHtml !== undefined && currentHtml !== lastSavedContentRef.current) {
          save(currentHtml).catch(() => {});
        }
      }
    };
  }, [save]);

  // Only reset content when switching to a different check-in (not on refetch)
  useEffect(() => {
    if (editor && checkInId !== checkInIdRef.current) {
      editor.commands.setContent(initialContent || '');
      lastSavedContentRef.current = initialContent;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInId, editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border bg-white shadow-sm dark:bg-zinc-900',
        className,
      )}
    >
      {/* Save indicator — subtle top-right text */}
      <div className="flex items-center justify-end px-3 py-1">
        <span
          className={cn(
            'text-[11px] font-medium transition-opacity',
            saveStatus === 'idle' && 'opacity-0',
            saveStatus === 'saving' && 'text-muted-foreground',
            saveStatus === 'saved' && 'text-muted-foreground',
            saveStatus === 'error' && 'text-destructive',
          )}
        >
          {saveStatus === 'saving' && 'Saving...'}
          {saveStatus === 'saved' && 'Saved'}
          {saveStatus === 'error' && 'Save failed'}
        </span>
      </div>

      {/* Editor */}
      <EditorContent
        editor={editor}
        className={cn(
          'flex-1',
          '[&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[200px]',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
        )}
      />
    </div>
  );
};
